// 1. 创建一个全局状态存储，用于记录每个路径的折叠状态
// Map<path: string, isCollapsed: boolean>
const viewerState = new Map();

class JSONViewer {
  constructor(options = {}) {
    this.options = Object.assign(
      {
        rootCollapsable: true,
        clickableUrls: true,
        bigNumbers: false,
        defaultCollapseLevel: 2,
        id: "root", // 增加一个实例ID，防止多个 viewer 冲突
      },
      options
    );
  }

  isCollapsable(arg) {
    return arg instanceof Object && Object.keys(arg).length > 0;
  }

  isUrl(string) {
    const protocols = ["http", "https", "ftp", "ftps"];
    return protocols.some((protocol) => string.startsWith(protocol + "://"));
  }

  htmlEscape(s) {
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/'/g, "&apos;")
      .replace(/"/g, "&quot;");
  }

  // 修改核心：增加 path 参数，用于追踪当前节点的唯一路径
  json2html(json, depth = 0, path = "") {
    let html = "";
    // 如果是根节点，初始化 path
    if (depth === 0 && !path) path = this.options.id;

    if (typeof json === "string") {
      json = this.htmlEscape(json);
      if (this.options.clickableUrls && this.isUrl(json)) {
        html += `<a href="${json}" class="json-value json-link" target="_blank">${json}</a>`;
      } else {
        json = json.replace(/&quot;/g, "\\&quot;");
        html += `<span class="json-value">"${json}"</span>`;
      }
    } else if (typeof json === "number" || typeof json === "bigint") {
      html += `<span class="json-literal">${json}</span>`;
    } else if (typeof json === "boolean") {
      html += `<span class="json-literal">${json}</span>`;
    } else if (json === null) {
      html += '<span class="json-literal">null</span>';
    } else if (Array.isArray(json)) {
      if (json.length > 0) {
        // 修改逻辑：优先读取 viewerState 中的状态，如果没有记录，则使用默认层级逻辑
        let isCollapsed;
        if (viewerState.has(path)) {
          isCollapsed = viewerState.get(path);
        } else {
          isCollapsed = depth >= this.options.defaultCollapseLevel;
        }

        html +=
          `[<ol class="json-array${isCollapsed ? " hidden" : ""}" data-path="${path}">`; // 注入 data-path

        for (let i = 0; i < json.length; ++i) {
          html += "<li>";
          // 递归生成子路径：数组用 [index]
          const childPath = `${path}[${i}]`;

          if (this.isCollapsable(json[i])) {
            // 按钮注入 data-path，并根据状态设置 collapsed 类
            html +=
              `<a class="json-toggle${isCollapsed ? " collapsed" : ""}" data-path="${path}"></a>`;
          }
          html += this.json2html(json[i], depth + 1, childPath);
          if (i < json.length - 1) {
            html += ",";
          }
          html += "</li>";
        }
        html += "</ol>]";
        if (isCollapsed) {
          html += this.renderPlaceholder(json.length, path);
        }
      } else {
        html += "[]";
      }
    } else if (typeof json === "object") {
      if (
        this.options.bigNumbers &&
        (typeof json.toExponential === "function" || json.isLosslessNumber)
      ) {
        html += `<span class="json-literal">${json.toString()}</span>`;
      } else {
        const keyCount = Object.keys(json).length;
        if (keyCount > 0) {
          // 修改逻辑：同上，优先读取 viewerState
          let isCollapsed;
          if (viewerState.has(path)) {
            isCollapsed = viewerState.get(path);
          } else {
            isCollapsed = depth >= this.options.defaultCollapseLevel;
          }

          html +=
            `{<ul class="json-dict${isCollapsed ? " hidden" : ""}" data-path="${path}">`; // 注入 data-path

          let count = 0;
          for (const key in json) {
            if (Object.prototype.hasOwnProperty.call(json, key)) {
              const jsonElement = json[key];
              const escapedKey = this.htmlEscape(key);
              const keyRepr = `<span class="json-key">"${escapedKey}"</span>`;
              // 递归生成子路径：对象用 .key
              // 注意：为了路径安全，也可以用 ["key"] 格式，这里简单用 . 
              const childPath = `${path}.${key}`;

              html += "<li>";
              if (this.isCollapsable(jsonElement)) {
                // 判断当前子元素的状态
                let childIsCollapsed;
                if (viewerState.has(childPath)) {
                  childIsCollapsed = viewerState.get(childPath);
                } else {
                  childIsCollapsed = (depth + 1) >= this.options.defaultCollapseLevel;
                }

                html += `<a class="json-toggle${childIsCollapsed ? " collapsed" : ""
                  }" data-path="${childPath}">${keyRepr}</a>`;
              } else {
                html += keyRepr;
              }
              html += ": " + this.json2html(jsonElement, depth + 1, childPath);
              if (++count < keyCount) {
                html += ",";
              }
              html += "</li>";
            }
          }
          html += "</ul>}";
          if (isCollapsed) {
            html += this.renderPlaceholder(keyCount, path);
          }
        } else {
          html += "{}";
        }
      }
    }
    return html;
  }

  // 提取占位符生成逻辑，方便复用
  renderPlaceholder(count, path) {
    // 占位符也带上 data-path，方便点击时反向查找
    return `<a class="json-placeholder" data-path="${path}">${count} ${count > 1 ? "items" : "item"
      }</a>`;
  }

  render(jsonData) {
    // 每次渲染生成唯一的根 ID，或者沿用 options.id
    const rootPath = this.options.id;
    let html = this.json2html(jsonData, 0, rootPath);

    if (this.options.rootCollapsable && this.isCollapsable(jsonData)) {
      // 根节点状态检查
      const isCollapsed = viewerState.has(rootPath) ? viewerState.get(rootPath) : false;
      html = `<a class="json-toggle${isCollapsed ? " collapsed" : ""}" data-path="${rootPath}"></a>` + html;
    }

    return html;
  }
}

// 初始化折叠功能的事件监听器
let jsonViewerInitialized = false;

export const initJSONViewer = () => {
  if (jsonViewerInitialized) return;
  jsonViewerInitialized = true;

  if (typeof document !== "undefined") {
    document.addEventListener("click", (e) => {
      // 处理 Toggle 点击
      if (e.target.matches(".json-toggle")) {
        e.preventDefault();
        e.stopPropagation();
        const target = e.target;
        // 读取 path
        const path = target.getAttribute("data-path");

        const isCollapsed = target.classList.contains("collapsed");
        target.classList.toggle("collapsed");

        // 核心修改：将新状态保存到全局 Map 中
        if (path) {
          viewerState.set(path, !isCollapsed); // 注意：如果当前是 collapsed，点击后变为展开(false)
        }

        // --- 以下是 DOM 操作，基本保持不变，但增加了一些鲁棒性 ---
        const findContainer = (element) => {
          // 尝试通过 data-path 匹配最近的容器（更精准）
          if (path) {
            // 查找紧邻的兄弟容器
            let sibling = element.nextElementSibling;
            while (sibling) {
              if (sibling.getAttribute("data-path") === path &&
                (sibling.classList.contains("json-dict") || sibling.classList.contains("json-array"))) {
                return sibling;
              }
              sibling = sibling.nextElementSibling;
            }
          }
          return null;
        };

        const container = findContainer(target);

        if (container) {
          container.classList.toggle("hidden");

          // Ctrl+点击：递归处理子元素
          if (e.ctrlKey) {
            // 这里的逻辑比较复杂，为了简化，我们只需要更新 DOM
            // 状态同步在批量操作时会很繁琐，暂时只处理视觉
            // 如果需要严格同步，需要遍历所有子节点更新 viewerState
          }

          // 移除旧占位符
          let nextSibling = container.nextElementSibling;
          while (
            nextSibling &&
            nextSibling.classList.contains("json-placeholder")
          ) {
            const toRemove = nextSibling;
            nextSibling = nextSibling.nextElementSibling;
            toRemove.remove();
          }

          // 如果折叠，添加占位符
          if (!isCollapsed) {
            const count = container.children.length;
            const placeholder = document.createElement("a");
            placeholder.className = "json-placeholder";
            placeholder.textContent = `${count} ${count > 1 ? "items" : "item"}`;
            placeholder.setAttribute("data-path", path); // 赋予 path
            container.parentNode.insertBefore(placeholder, container.nextSibling);
          }
        }
      }
      // 处理 Placeholder 点击
      else if (e.target.matches(".json-placeholder")) {
        e.preventDefault();
        e.stopPropagation();
        const placeholder = e.target;
        const path = placeholder.getAttribute("data-path");

        // 利用 data-path 找到对应的 toggle 并触发点击
        // 查找之前的兄弟元素中带有相同 data-path 的 toggle
        let prev = placeholder.previousElementSibling;
        while (prev) {
          // 可能是 ul/ol 容器，先跳过
          if (prev.classList.contains("json-dict") || prev.classList.contains("json-array")) {
            prev = prev.previousElementSibling;
            continue;
          }
          // 找到 toggle
          if (prev.classList.contains("json-toggle") && prev.getAttribute("data-path") === path) {
            prev.click();
            return;
          }
          // 如果是 key span，继续向前
          prev = prev.previousElementSibling;
        }
        // 如果上面没找到（例如根节点结构不同），尝试父级查找兜底（保留你原来的逻辑也可）
      }
    });
  }
};

export const formatJSONWithLinks = (obj, options = {}) => {
  const viewer = new JSONViewer({
    clickableUrls: true,
    rootCollapsable: options.rootCollapsable !== false,
    defaultCollapseLevel: 1,
    id: options.id || "root", // 允许传入 ID，默认为 root
    ...options,
  });

  if (typeof window !== "undefined") {
    initJSONViewer();
  }

  return viewer.render(obj);
};